import { TokenExpirationHandler } from './tokenExpirationHandler';

/**
 * Helper class to handle specific MSAL timeout errors
 * that occur when the app is left open for a long time
 */
export class TimeoutErrorHandler {
  private static readonly TIMEOUT_ERROR_PATTERNS = [
    'monitor_window_timeout',
    'token acquisition in iframe failed due to timeout',
    'timed_out'
  ];

  /**
   * Checks if the error is a monitor window timeout error
   * These errors occur when silent token refresh fails because the iframe times out
   */
  static isTimeoutError(error: any): boolean {
    if (!error) return false;

    // Convert error to string for pattern matching
    const errorString = error instanceof Error ? error.toString() : String(error);
    const errorMessage = errorString.toLowerCase();

    // Check for specific timeout error patterns
    return this.TIMEOUT_ERROR_PATTERNS.some(pattern => errorMessage.includes(pattern));
  }

  /**
   * Handles timeout errors by clearing auth state and redirecting to login page
   * This should be called when monitor_window_timeout errors are detected
   */
  static async handleTimeoutError(error: any): Promise<void> {
    if (this.isTimeoutError(error)) {
      console.log('[TimeoutErrorHandler] Detected monitor_window_timeout error, initiating login redirect...');

      try {
        // Use the existing token expiration handler to handle the redirect
        // This ensures a consistent experience for users with expired sessions
        await TokenExpirationHandler.handleTokenExpiration(error);
      } catch (handlerError) {
        console.error('[TimeoutErrorHandler] Error handling timeout error:', handlerError);

        // Fallback: force redirect if the handler fails
        window.onbeforeunload = null;
        window.location.href = '/?sessionExpired=true';
      }
    }
  }

  /**
   * Attempts interactive login as a fallback when silent refresh fails
   * This should be called before giving up on authentication
   */
  static async attemptInteractiveLogin(): Promise<boolean> {
    try {
      // Use the TokenExpirationHandler's ability to detect if we're already redirecting
      // If true, this prevents multiple popups/redirects
      if ((TokenExpirationHandler as any).isRedirecting) {
        return false;
      }

      console.log('[TimeoutErrorHandler] Attempting interactive login as fallback');

      // Redirect to login page with a specific parameter
      // The auth provider will detect this and trigger an interactive login
      window.location.href = '/?interactiveLogin=true';
      return true;
    } catch (error) {
      console.error('[TimeoutErrorHandler] Error during interactive login attempt:', error);
      return false;
    }
  }
}
